import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, withKeys as _withKeys, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between h-full" }
const _hoisted_2 = { class: "flex justify-end" }
const _hoisted_3 = { class: "font-semibold text-gray-700 truncate" }
const _hoisted_4 = { class: "text-sm text-gray-500 truncate" }
const _hoisted_5 = { class: "flex justify-end" }
const _hoisted_6 = {
  key: 0,
  class: "text-center text-gray-500 py-3"
}
const _hoisted_7 = {
  key: 0,
  class: "text-[13px] leading-relaxed text-gray-500"
}
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { class: "font-semibold text-gray-700 break-words" }
const _hoisted_10 = {
  class: "btn-primary",
  type: "submit"
}

import { XCircleIcon } from '@heroicons/vue/24/outline'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

import NeueButton from '@/components/neue/NeueButton.vue'
import NeueTablist from '@/components/neue/NeueTablist.vue'
import SettingsSection from '@/components/neue/SettingsSection.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import TheModal from '@/components/TheModal.vue'
import SystemUserModal from '@/components/users/SystemUserModal.vue'
import { rolePath, ROLES_HOME } from '@/config/settingsTabs'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'
import SettingsShell from '@/view/settings/SettingsShell.vue'


export default {
  __name: 'RoleEdit',
  setup(__props) {

const route = useRoute()

const roleUsersCols = [
  { width: 'minmax(160px, 1fr)' }, // Users assigned to this role
  { width: '100px' } // "+ User" action button
]
const privilegeCols = [{ width: 'minmax(120px, 1fr)' }, { width: 'minmax(160px, 200px)' }]
const ACCESS_OPTIONS = [
  { name: 'No access', value: 'no_access' },
  { name: 'Read only', value: 'read_only' },
  { name: 'Read and write', value: 'read_write' }
]
const SELL_ACCESS_OPTIONS = ACCESS_OPTIONS.filter(option => option.value !== 'read_only')

const canCreateSystemUsers = computed(() => privilegeService.canAccessResourceAction('system_users', null, 'create'))
const canReadSystemUsers = computed(() => privilegeService.canAccessResourceAction('system_users', null, 'read'))
const canUpdateSystemUsers = computed(() => privilegeService.canAccessResourceAction('system_users', null, 'update'))
const canDeleteSystemUsers = computed(() => privilegeService.canAccessResourceAction('system_users', null, 'delete'))
const router = useRouter()

const isNew = computed(() => route.params.id === 'new')
const hasUnsavedChanges = ref(false)
const leaveModal = ref(false)
const pendingRoute = ref(null)
const deleteRoleModal = ref(false)
const name = ref('')
const originalName = ref('')
const userModal = reactive({
  open: false,
  id: null,
  full_name: '',
  email: '',
  roleId: null,
  originalFullName: '',
  originalEmail: '',
  originalRoleId: null
})

const role = computed(() => (isNew.value ? null : mapRelationship({ type: 'system_user_role', id: route.params.id })))
const isAdminRole = computed(() => !!role.value?.attributes?.is_admin)
const allSystemUsers = computed(() => mapType('system_user'))
const allRoles = computed(() => mapType('system_user_role'))
const canSubmit = computed(
  () =>
    (hasUnsavedChanges.value || isNew.value) && (isNew.value ? canCreateSystemUsers.value : canUpdateSystemUsers.value)
)
const roleSystemUsers = computed(() => {
  if (!role.value || !role.value.relationships?.system_users?.data) return []
  const systemUserIds = role.value.relationships.system_users.data.map(u => u.id)
  return allSystemUsers.value.filter(user => systemUserIds.includes(user.id))
})
const privilegesFromRole = computed(() => {
  if (!role.value || !role.value.relationships?.system_user_privileges?.data) return []
  const privilegeObjects = mapRelationships(role.value.relationships.system_user_privileges.data)
  return privilegeObjects.map(privilege => ({
    id: privilege.id,
    resource: privilege.attributes.resource,
    sub_resource: privilege.attributes.sub_resource,
    can_create: privilege.attributes.can_create,
    can_read: privilege.attributes.can_read,
    can_update: privilege.attributes.can_update,
    can_delete: privilege.attributes.can_delete
  }))
})

const privileges = ref([])

const individualResources = [
  { value: 'orders', label: 'Orders' },
  { value: 'messages', label: 'Messages' },
  { value: 'customers', label: 'Customers' },
  { value: 'sell', label: 'Sell' },
  { value: 'site', label: 'Site settings' },
  { value: 'automation', label: 'Automation' },
  { value: 'reports', label: 'Reports' },
  { value: 'system_users', label: 'System users' },
  { value: 'customer_profile', label: 'Customer profile' }
]

const getResourcePrivilege = resource => {
  const specific = privileges.value.find(p => p.resource === resource && !p.sub_resource && !p._destroy)
  if (specific) return specific

  const global = privileges.value.find(p => p.resource === 'global' && !p.sub_resource && !p._destroy)
  return global
}

const fetchRole = id => api.call('GET', `system_user_roles/${id}?included=system_users,system_user_privileges`)

const loadRole = (id = route.params.id) => {
  if (id === 'new' || !id) {
    name.value = ''
    originalName.value = ''
    privileges.value = []
    return
  }

  return fetchRole(id)
}

const resetPrivileges = () => {
  privileges.value = privilegesFromRole.value.map(privilege => ({ ...privilege }))
}

watch(
  [() => route.params.id, () => role.value?.attributes?.name],
  ([, newName]) => {
    if (!isNew.value && newName) {
      name.value = newName
      originalName.value = newName
    }
  },
  { immediate: true }
)

watch(
  [privilegesFromRole, () => route.params.id],
  () => {
    if (!isNew.value) resetPrivileges()
  },
  { immediate: true }
)

const del = () => {
  if (!canDeleteSystemUsers.value) return

  if (roleSystemUsers.value.length > 0) {
    deleteRoleModal.value = true
    return
  }

  if (!confirm('Delete this role?')) return
  api.call('DELETE', `system_user_roles/${route.params.id}`).then(() => {
    hasUnsavedChanges.value = false
    router.push(ROLES_HOME)
  })
}

const handleKeydown = event => {
  if (event.keyCode === 13 && event.target.localName !== 'textarea') event.preventDefault()
}

const handleSubmit = () => {
  if (isNew.value && !canCreateSystemUsers.value) return
  if (!isNew.value && !canUpdateSystemUsers.value) return

  const allResourcesReadWrite = individualResources.every(resource => {
    const mode = getResourcePrivilegeMode(resource.value)
    return mode === 'read_write'
  })

  let privilegesAttributes = []

  if (allResourcesReadWrite) {
    const existingResourcePrivs = privileges.value.filter(p => !p.sub_resource && p.resource !== 'global')
    existingResourcePrivs.forEach(priv => {
      if (priv.id) {
        privilegesAttributes.push({ id: priv.id, _destroy: true })
      }
    })

    const existingGlobal = privileges.value.find(p => p.resource === 'global' && !p.sub_resource)

    if (existingGlobal && existingGlobal.id) {
      privilegesAttributes.push({
        id: existingGlobal.id,
        resource: 'global',
        can_create: true,
        can_read: true,
        can_update: true,
        can_delete: true
      })
    } else {
      privilegesAttributes.push({
        resource: 'global',
        can_create: true,
        can_read: true,
        can_update: true,
        can_delete: true
      })
    }
  } else {
    const existingGlobal = privileges.value.find(p => p.resource === 'global' && !p.sub_resource && !p._destroy)
    if (existingGlobal && existingGlobal.id) {
      privilegesAttributes.push({ id: existingGlobal.id, _destroy: true })
    }

    individualResources.forEach(resource => {
      const priv = getResourcePrivilege(resource.value)
      if (priv && priv.resource !== 'global' && !priv._destroy) {
        const attr = {
          resource: priv.resource,
          can_create: priv.can_create || false,
          can_read: priv.can_read || false,
          can_update: priv.can_update || false,
          can_delete: priv.can_delete || false
        }
        if (priv.id) {
          attr.id = priv.id
        }
        privilegesAttributes.push(attr)
      }
    })
  }

  // Clean up any existing sell sub-resource privileges
  const existingSubResourcePrivs = privileges.value.filter(p => p.resource === 'sell' && p.sub_resource && !p._destroy)
  existingSubResourcePrivs.forEach(priv => {
    if (priv.id) {
      privilegesAttributes.push({ id: priv.id, _destroy: true })
    }
  })

  if (!isNew.value) {
    const destroyedPrivs = privileges.value
      .filter(priv => priv._destroy && priv.id)
      .map(priv => ({ id: priv.id, _destroy: true }))
    privilegesAttributes.push(...destroyedPrivs)
  }

  if (isNew.value) {
    api
      .call('POST', 'system_user_roles', {
        system_user_role: {
          name: name.value,
          system_user_privileges_attributes: privilegesAttributes
        }
      })
      .then(response => {
        hasUnsavedChanges.value = false
        const id = response.data.data.id
        fetchRole(id).then(() => router.push(rolePath(id)))
      })
  } else {
    api
      .call('PATCH', `system_user_roles/${route.params.id}`, {
        system_user_role: {
          name: name.value,
          system_user_privileges_attributes: privilegesAttributes
        }
      })
      .then(() => {
        hasUnsavedChanges.value = false
        originalName.value = name.value
        loadRole()
      })
  }
}

const handleDiscardChanges = () => {
  name.value = originalName.value
  resetPrivileges()
  hasUnsavedChanges.value = false
  loadRole()
}

const handleConfirmCancelChanges = () => {
  hasUnsavedChanges.value = false
  leaveModal.value = false
  router.push(pendingRoute.value ?? ROLES_HOME)
}

const addUser = () => {
  if (!canCreateSystemUsers.value) return
  userModal.id = null
  userModal.roleId = !isNew.value ? route.params.id : null
  userModal.open = true
  userModal.full_name = ''
  userModal.email = ''
  userModal.originalRoleId = null
}

const editUser = systemUser => {
  if (!canUpdateSystemUsers.value) return
  userModal.id = systemUser.id
  userModal.roleId = systemUser.relationships?.system_user_role?.data?.id || null
  userModal.open = true
  userModal.full_name = systemUser.attributes.full_name || ''
  userModal.email = systemUser.attributes.email || ''
  userModal.originalFullName = systemUser.attributes.full_name || ''
  userModal.originalEmail = systemUser.attributes.email || ''
  userModal.originalRoleId = systemUser.relationships?.system_user_role?.data?.id || null
}

const removeUser = async systemUser => {
  if (!canDeleteSystemUsers.value) return
  if (systemUser.attributes.default_merchant_user) {
    alert('This is the default merchant admin user and cannot be deleted.')
    return
  }
  if (!confirm('Remove this user from the role?')) return

  try {
    await api.call('DELETE', `system_users/${systemUser.id}`)
    await fetchRole(route.params.id)
  } catch (error) {
    console.error('Error removing user:', error)
  }
}

const saveUserModal = async user => {
  if (!user.full_name || !user.email) return

  if (user.id && !canUpdateSystemUsers.value) return
  if (!user.id && !canCreateSystemUsers.value) return

  userModal.open = false

  try {
    if (user.id) {
      const updateData = {
        system_user: {
          full_name: user.full_name,
          email: user.email
        }
      }
      if (user.roleId !== user.originalRoleId) {
        updateData.system_user.system_user_role_id = parseInt(user.roleId)
      }
      await api.call('PATCH', `system_users/${user.id}`, updateData)
    } else {
      await api.call('POST', 'system_users', {
        system_user: {
          full_name: user.full_name,
          email: user.email,
          system_user_role_id: parseInt(user.roleId || route.params.id)
        }
      })
    }

    await api.call('GET', `system_users?included=none`)
    await fetchRole(route.params.id)
  } catch (error) {
    console.error('Error saving user:', error)
  }
}

const getResourcePrivilegeMode = resource => {
  const privilege = getResourcePrivilege(resource)
  if (!privilege) return 'no_access'

  if (privilege.can_read) {
    if (privilege.can_create || privilege.can_update || privilege.can_delete) {
      return 'read_write'
    }
    // For sell, there's no read_only option - treat as read_write if they have read access
    if (resource === 'sell') {
      return 'read_write'
    }
    return 'read_only'
  }
  return 'no_access'
}

const updateResourcePrivilegeMode = (resource, mode) => {
  if (!canUpdateSystemUsers.value) return
  hasUnsavedChanges.value = true

  const existing = getResourcePrivilege(resource)
  const isGlobal = existing && existing.resource === 'global'

  if (isGlobal && resource !== 'global') {
    if (existing.id) {
      existing._destroy = true
    } else {
      const index = privileges.value.indexOf(existing)
      if (index > -1) privileges.value.splice(index, 1)
    }

    individualResources.forEach(r => {
      if (r.value === resource) {
        if (mode !== 'no_access') {
          privileges.value.push({
            resource: r.value,
            can_read: true,
            can_create: mode === 'read_write',
            can_update: mode === 'read_write',
            can_delete: mode === 'read_write'
          })
        }
      } else {
        privileges.value.push({
          resource: r.value,
          can_read: true,
          can_create: true,
          can_update: true,
          can_delete: true
        })
      }
    })
    return
  }

  if (mode === 'no_access' || mode === null) {
    if (existing) {
      if (existing.id) {
        existing._destroy = true
      } else {
        const index = privileges.value.indexOf(existing)
        if (index > -1) privileges.value.splice(index, 1)
      }
    }
    return
  }

  if (existing && !isGlobal) {
    if (mode === 'read_only') {
      existing.can_read = true
      existing.can_create = false
      existing.can_update = false
      existing.can_delete = false
    } else if (mode === 'read_write') {
      existing.can_read = true
      existing.can_create = true
      existing.can_update = true
      existing.can_delete = true
    }
    existing._destroy = false
  } else {
    const newPrivilege = {
      resource: resource,
      can_read: true,
      can_create: mode === 'read_write',
      can_update: mode === 'read_write',
      can_delete: mode === 'read_write'
    }
    privileges.value.push(newPrivilege)
  }
}

const holdForUnsavedChanges = to => {
  if (!hasUnsavedChanges.value) return true
  pendingRoute.value = to.fullPath
  leaveModal.value = true
  return false
}

onBeforeRouteLeave(to => holdForUnsavedChanges(to))

onBeforeRouteUpdate(to => {
  if (to.params.id === route.params.id) return true
  if (!holdForUnsavedChanges(to)) return false
  loadRole(to.params.id)
  return true
})

onMounted(() => {
  if (canReadSystemUsers.value) {
    Promise.all([
      api.call('GET', 'system_users?included=none'),
      api.call('GET', 'system_user_roles?included=system_users,system_user_privileges'),
      loadRole()
    ])
  } else {
    loadRole()
  }
})

return (_ctx, _cache) => {
  const _component_SaveBar = _resolveComponent("SaveBar")
  const _component_InputField = _resolveComponent("InputField")
  const _component_SelectInput = _resolveComponent("SelectInput")
  const _component_PageFooter = _resolveComponent("PageFooter")

  return (_openBlock(), _createBlock(SettingsShell, {
    parent: { link: _unref(ROLES_HOME), name: 'All roles' }
  }, {
    savebar: _withCtx(() => [
      (!isNew.value && canUpdateSystemUsers.value)
        ? (_openBlock(), _createBlock(_component_SaveBar, {
            key: 0,
            "has-unsaved-changes": hasUnsavedChanges.value,
            onOnDiscard: _cache[0] || (_cache[0] = $event => (handleDiscardChanges())),
            onOnSave: _cache[1] || (_cache[1] = $event => (handleSubmit()))
          }, null, 8, ["has-unsaved-changes"]))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("form", {
          class: "flex flex-1 flex-col justify-between",
          onSubmit: _cache[4] || (_cache[4] = _withModifiers($event => (handleSubmit()), ["prevent"])),
          onKeydown: [
            _cache[5] || (_cache[5] = _withKeys($event => (handleKeydown($event)), ["enter"])),
            _cache[6] || (_cache[6] = _withKeys(_withModifiers($event => (handleSubmit()), ["meta","exact"]), ["enter"]))
          ]
        }, [
          _createElementVNode("div", null, [
            _createVNode(SettingsSection, {
              title: "Role name",
              description: "What your team sees when they assign this role."
            }, {
              default: _withCtx(() => [
                _createVNode(_component_InputField, {
                  modelValue: name.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((name).value = $event)),
                  "aria-label": "Role name",
                  disabled: (isNew.value && !canCreateSystemUsers.value) || (!isNew.value && !canUpdateSystemUsers.value) || isAdminRole.value,
                  onInput: _cache[3] || (_cache[3] = $event => (hasUnsavedChanges.value = true))
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            (!isNew.value)
              ? (_openBlock(), _createBlock(SettingsSection, {
                  key: 0,
                  title: "Users",
                  description: "The team members who sign in with this role. Adding, editing or removing a user saves straight away.",
                  plain: ""
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", null, [
                      _createVNode(NeueTablist, null, {
                        head: _withCtx(() => [
                          _createVNode(TablistHeader, null, {
                            default: _withCtx(() => [
                              _createVNode(TablistGrid, {
                                columns: roleUsersCols,
                                header: "",
                                class: "items-center"
                              }, {
                                default: _withCtx(() => [
                                  _cache[14] || (_cache[14] = _createElementVNode("div", null, "Name", -1)),
                                  _createElementVNode("div", _hoisted_2, [
                                    (canCreateSystemUsers.value)
                                      ? (_openBlock(), _createBlock(NeueButton, {
                                          key: 0,
                                          type: "secondary",
                                          onClick: addUser
                                        }, {
                                          default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                            _createTextVNode("+ User", -1)
                                          ]))]),
                                          _: 1
                                        }))
                                      : _createCommentVNode("", true)
                                  ])
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roleSystemUsers.value, (systemUser) => {
                            return (_openBlock(), _createBlock(TablistRow, {
                              key: systemUser.id,
                              class: _normalizeClass(["transition-colors", canUpdateSystemUsers.value ? 'cursor-pointer' : 'cursor-not-allowed opacity-50']),
                              onClick: $event => (canUpdateSystemUsers.value && editUser(systemUser))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(TablistGrid, {
                                  columns: roleUsersCols,
                                  class: "items-center"
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", null, [
                                      _createElementVNode("div", _hoisted_3, _toDisplayString(systemUser.attributes.full_name || systemUser.attributes.email), 1),
                                      _createElementVNode("div", _hoisted_4, _toDisplayString(systemUser.attributes.email), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_5, [
                                      (canDeleteSystemUsers.value && !systemUser.attributes.default_merchant_user)
                                        ? (_openBlock(), _createBlock(_unref(XCircleIcon), {
                                            key: 0,
                                            class: "h-5 w-5 cursor-pointer text-gray-400",
                                            onClick: _withModifiers($event => (removeUser(systemUser)), ["stop"])
                                          }, null, 8, ["onClick"]))
                                        : _createCommentVNode("", true)
                                    ])
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1032, ["class", "onClick"]))
                          }), 128)),
                          (!roleSystemUsers.value.length)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _cache[16] || (_cache[16] = _createElementVNode("div", { class: "mb-2" }, "No users assigned to this role", -1)),
                                (canCreateSystemUsers.value)
                                  ? (_openBlock(), _createBlock(NeueButton, {
                                      key: 0,
                                      type: "secondary",
                                      onClick: addUser
                                    }, {
                                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                        _createTextVNode("+ User", -1)
                                      ]))]),
                                      _: 1
                                    }))
                                  : _createCommentVNode("", true)
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      })
                    ])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(SettingsSection, {
              title: "Privileges",
              description: "What people with this role can see and change in each area of the main navigation.",
              plain: !isAdminRole.value
            }, {
              default: _withCtx(() => [
                (isAdminRole.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_7, " The Administrator role has full access across the entire system. This includes all navigation areas, settings, and user management. Privileges for this role cannot be modified. "))
                  : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createVNode(NeueTablist, null, {
                        head: _withCtx(() => [
                          _createVNode(TablistHeader, null, {
                            default: _withCtx(() => [
                              _createVNode(TablistGrid, {
                                columns: privilegeCols,
                                header: "",
                                class: "items-center"
                              }, {
                                default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                  _createElementVNode("div", null, "Area", -1),
                                  _createElementVNode("div", null, "Access", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(individualResources, (resource) => {
                            return _createVNode(TablistRow, {
                              key: resource.value
                            }, {
                              default: _withCtx(() => [
                                _createVNode(TablistGrid, {
                                  columns: privilegeCols,
                                  class: "items-center"
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_9, _toDisplayString(resource.label), 1),
                                    _createVNode(_component_SelectInput, {
                                      disabled: !canUpdateSystemUsers.value,
                                      "model-value": getResourcePrivilegeMode(resource.value),
                                      options: resource.value === 'sell' ? _unref(SELL_ACCESS_OPTIONS) : ACCESS_OPTIONS,
                                      placeholder: "Select permission",
                                      "onUpdate:modelValue": value => updateResourcePrivilegeMode(resource.value, value)
                                    }, null, 8, ["disabled", "model-value", "options", "onUpdate:modelValue"])
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1024)
                          }), 64))
                        ]),
                        _: 1
                      })
                    ]))
              ]),
              _: 1
            }, 8, ["plain"]),
            (!isNew.value && canDeleteSystemUsers.value && !isAdminRole.value)
              ? (_openBlock(), _createBlock(SettingsSection, {
                  key: 1,
                  title: "Actions",
                  description: "A role can only be deleted once no users are assigned to it."
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", null, [
                      _createVNode(NeueButton, {
                        type: "red",
                        onClick: _withModifiers(del, ["prevent"])
                      }, {
                        default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                          _createTextVNode("Delete role", -1)
                        ]))]),
                        _: 1
                      })
                    ])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          (canSubmit.value)
            ? (_openBlock(), _createBlock(_component_PageFooter, { key: 0 }, {
                right: _withCtx(() => [
                  _createElementVNode("button", _hoisted_10, _toDisplayString(isNew.value ? 'Create' : 'Save'), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ], 32),
        _withDirectives(_createVNode(TheModal, {
          "is-open": leaveModal.value,
          "modal-title": "Abandon changes",
          "modal-description": "Are you sure you'd like to leave without saving your changes?",
          onCloseModal: _cache[9] || (_cache[9] = $event => (leaveModal.value = false))
        }, {
          footer: _withCtx(() => [
            _createElementVNode("button", {
              class: "btn-alert justify-center mr-3",
              onClick: _cache[7] || (_cache[7] = $event => (handleConfirmCancelChanges()))
            }, "Leave"),
            _createElementVNode("button", {
              class: "btn-ghost justify-center",
              type: "button",
              onClick: _cache[8] || (_cache[8] = $event => (leaveModal.value = false))
            }, "Stay")
          ]),
          _: 1
        }, 8, ["is-open"]), [
          [_vShow, leaveModal.value]
        ]),
        (canCreateSystemUsers.value || canUpdateSystemUsers.value)
          ? (_openBlock(), _createBlock(SystemUserModal, {
              key: 0,
              "is-open": userModal.open,
              user: userModal,
              roles: allRoles.value,
              "fixed-role-id": !isNew.value && !userModal.id ? _unref(route).params.id : null,
              onClose: _cache[10] || (_cache[10] = $event => (userModal.open = false)),
              onSave: saveUserModal
            }, null, 8, ["is-open", "user", "roles", "fixed-role-id"]))
          : _createCommentVNode("", true),
        _createVNode(TheModal, {
          "is-open": deleteRoleModal.value,
          "modal-title": "Unable to delete role",
          onCloseModal: _cache[12] || (_cache[12] = $event => (deleteRoleModal.value = false))
        }, {
          main: _withCtx(() => [...(_cache[19] || (_cache[19] = [
            _createElementVNode("p", { class: "text-gray-700" }, " This role cannot be deleted while it has users assigned to it. Please reassign or remove all assigned users before deleting this role. ", -1)
          ]))]),
          footer: _withCtx(() => [
            _createVNode(NeueButton, {
              type: "primary",
              onClick: _cache[11] || (_cache[11] = $event => (deleteRoleModal.value = false))
            }, {
              default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                _createTextVNode("OK", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["is-open"])
      ])
    ]),
    _: 1
  }, 8, ["parent"]))
}
}

}